import React, {useState, useEffect} from 'react';
import {observer} from 'mobx-react';
import {Modal, ModalButtons} from 'components/modals';
import {CheckboxWithLabel, Input, Select2, QuickTip, Spinner} from 'components';
import {t, api} from 'shared/core';
import {endpoints, formatTimeOffAmount} from 'main/tools';
import _ from 'lodash';

const UNITS = [
  'days',
  'hours'
];

const useForecastUnitChange = (type, originalUnit) => {
  const [forecast, setForecast] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (type.isNew || type.unit === originalUnit) {
      setForecast(null);
      return;
    }

    const fetchForecast = async () => {
      setIsLoading(true);
      const response = await api.post(
        endpoints.TIME_OFF.TYPES.FORECAST_UNIT_CHANGE.with(type.id),
        { unit: type.unit }
      );
      setForecast(response.data);
      setIsLoading(false);
    };

    fetchForecast();
  }, [type.unit, type.id, originalUnit]);

  return { forecast, isLoading };
};

const ForecastPreview = observer(({forecast, type, originalUnit}) => {
  if (type.isNew || !forecast || _.isEmpty(forecast)) {
    return null;
  }

  const paragraphs = [];

  forecast.forEach(policy => {
    const beforeValue = formatTimeOffAmount(policy.before, originalUnit);
    const afterValue = formatTimeOffAmount(policy.after, type.unit);

    paragraphs.push(`${policy.name}: ${beforeValue} → ${afterValue}`);
  });

  return (
    <QuickTip
      header={t('time_off.types.Impact of Unit Change on Policy Accrual Rates')}
      paragraphs={paragraphs}
      trait='info'
    />
  );
});

const EditTypeModal = observer(({uiState}) => {
  const {errors, editingType, originalUnit, editMessage} = uiState;

  const {forecast, isLoading} = useForecastUnitChange(
    editingType,
    originalUnit
  );

  return (
    <Modal
      isOpen={uiState.editTypeModalOpen}
      onHide={() => uiState.closeEditTypeModal()}
      size='sm'
      title={editMessage}>
      <Input
        label='time_off.types.Name'
        value={editingType.name}
        errorMessage={errors.name}
        onChange={(e) => editingType.merge({name: e.target.value})}
      />
      <div className='mt2'>
        <Select2
          label='time_off.types.Unit'
          value={editingType.unit}
          onChange={(e) => editingType.merge({unit: e.target.value})}
          errorMessage={errors.unit}
        >
          {UNITS.map(e => <option key={e} value={e}>{t(`time_off.types.units.${e}`)}</option>)}
        </Select2>
      </div>
      {isLoading && (
        <div className='mt2'>
          <Spinner />
        </div>
      )}
      {forecast && !isLoading && (
        <div className='mt2'>
          <ForecastPreview forecast={forecast} type={editingType} originalUnit={originalUnit}/>
        </div>
      )}
      <CheckboxWithLabel
        className='mt2'
        label='time_off.types.Include in time tracking'
        checked={editingType.includedInTimeTracking}
        onChange={(e) => editingType.includedInTimeTracking = e.checked}
      />
      <ModalButtons onSave={() => uiState.saveType()} onCancel={() => uiState.closeEditTypeModal()} />
    </Modal>
  );
});

export default EditTypeModal;
